import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { ForgotPasswordPage, RequireAuth, RequirePermission, SignInPage } from "@/features/auth";
import { ANNOUNCEMENT_ACCESS, PERMISSIONS } from "@/lib/permissions";
import { NotFoundPage, RouteError } from "./RouteError";
import { UNBUILT_SECTIONS } from "./unbuilt-sections";

/**
 * Routes.
 *
 * Three kinds of destination, and the difference between them is what the API
 * serves rather than what the panel can draw:
 *
 *   - **Built.** The overview, players & moderation, notifications,
 *     announcements, admins & roles, and your own profile. These call `/v1/admin/*` for real.
 *   - **Unbuilt.** Everything else in the nav. The API has no routes for them,
 *     so they cannot be opened: the nav row is disabled and the URL redirects
 *     to the overview — see `unbuilt-sections.tsx`. The feature components
 *     are still in `src/features`; putting one back is a one-line change here.
 *   - **Sign-in** and forgot-password, outside the shell and outside the
 *     guard.
 *
 * Built pages are lazy — the profile screens pull in an upload flow and the
 * role editor pulls in a large permission grid, and a support agent who only
 * ever opens the roster should download neither. The unbuilt pages are not:
 * they are one shared component over a table of strings, and code-splitting
 * eighteen copies of it would cost a network round trip to render a paragraph.
 *
 * `RequirePermission` guards each section as well as the nav hiding it. Nav
 * hiding alone is not a gate: routes are reachable by URL, and a shared link
 * should explain itself rather than render an empty screen. It stays on the
 * unbuilt sections too — the permission is what decides whether this person
 * would ever have seen the section, and that does not change just because it
 * is not finished.
 */
const page = (loader: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(loader);
  return (
    <Suspense
      fallback={
        <div className="p-8 text-center" role="status" aria-live="polite">
          <span className="text-content-muted text-sm">Loading…</span>
        </div>
      }
    >
      <Component />
    </Suspense>
  );
};

const router = createBrowserRouter([
  {
    // Top-level catch: sign-in, the auth guard, or the shell itself failing.
    // Full screen, because there may be no shell left to draw inside.
    errorElement: <RouteError fullScreen />,
    children: [
      { path: "/sign-in", element: <SignInPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },

      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                // Errors in a page render inside the shell, so the sidebar stays
                // usable and the person can simply navigate somewhere else.
                errorElement: <RouteError />,
                children: [
                  {
                    index: true,
                    element: page(() => import("@/features/dashboard/DashboardPage")),
                  },

                  // Generated, so the nav markers and the routes cannot drift
                  // apart. Not openable: the nav row is disabled, and a typed
                  // or bookmarked URL lands back on the overview.
                  ...UNBUILT_SECTIONS.map((section) => ({
                    path: section.path,
                    element: <Navigate to="/" replace />,
                  })),

                  {
                    path: "players",
                    element: <RequirePermission permission={PERMISSIONS.PLAYER_VIEW} />,
                    children: [
                      {
                        index: true,
                        element: page(() => import("@/features/players/PlayersPage")),
                      },
                      {
                        path: ":id",
                        element: page(() => import("@/features/players/PlayerDetailPage")),
                      },
                    ],
                  },

                  {
                    path: "notifications",
                    element: <RequirePermission permission={PERMISSIONS.NOTIFICATION_VIEW} />,
                    children: [
                      {
                        index: true,
                        element: page(() => import("@/features/notifications/NotificationsPage")),
                      },
                    ],
                  },

                  {
                    path: "announcements",
                    element: <RequirePermission permission={ANNOUNCEMENT_ACCESS} />,
                    children: [
                      {
                        index: true,
                        element: page(() => import("@/features/announcements/AnnouncementsPage")),
                      },
                    ],
                  },
                  // The section used to be called Content; old links still land.
                  { path: "content", element: <Navigate to="/announcements" replace /> },

                  {
                    path: "admins",
                    // Owner only: managing admins and roles is never delegated.
                    element: <RequirePermission permission={PERMISSIONS.ADMIN_VIEW} ownerOnly />,
                    children: [
                      { index: true, element: page(() => import("@/features/admins/AdminsPage")) },
                    ],
                  },

                  /*
            Your own account. No `RequirePermission`: every endpoint behind it
            is scoped to the caller by the token, so there is nobody else to
            protect. `/settings` redirects rather than 404ing — the breadcrumb
            on the page reads "Settings › Profile", and a URL people can see is
            a URL people will trim.
          */
                  {
                    path: "settings",
                    children: [
                      { index: true, element: <Navigate to="/settings/profile" replace /> },
                      {
                        path: "profile",
                        element: page(() => import("@/features/profile/ProfilePage")),
                      },
                    ],
                  },

                  { path: "*", element: <NotFoundPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
